import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { IPagingOptions, PagingOptions } from '../models/paged-options';
import { PagedList } from '../models/paged-list';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseApiService<T> {
  /**
   * The base API url
   */
  protected baseApiUrl: string;

  /**
   * The API url for a specific entity.
   * baseApiUrl/id
   * @param id The entity Id
   */
  protected idApiUrl = (id: any) => `${this.baseApiUrl}/${id}`;

  constructor(protected httpClient: HttpClient, baseApiUrl: string) {
    this.baseApiUrl = baseApiUrl;
  }

  /**
   * Get a list of entities
   * @returns An array of entities
   */
  protected getList(queryParams: any | undefined = undefined, overrideUrl: string | undefined = undefined): Observable<T[]> {
    let httpParams = new HttpParams();
    httpParams = BaseApiService.populateHttpParams(queryParams, httpParams);

    const options = { params: httpParams };

    const url = overrideUrl ?? this.baseApiUrl;

    return this.httpClient.get<T[]>(url, options);
  }

  /**
   * Get a paged list of entities
   * @returns A paged list of entities
   */
  protected getPagedList<TPage = T>(
    pageOptions: IPagingOptions | undefined = undefined,
    overrideUrl: string | undefined = undefined,
    additionalQueryParams: any | undefined = undefined
  ): Observable<PagedList<TPage>> {
    pageOptions ??= new PagingOptions();

    let httpParams = new HttpParams();
    httpParams = httpParams.append('pageNumber', pageOptions.pageNumber);
    httpParams = httpParams.append('pageSize', pageOptions.pageSize);

    if (additionalQueryParams !== undefined) {
      httpParams = BaseApiService.populateHttpParams(
        additionalQueryParams,
        httpParams
      );
    }

    const options = { params: httpParams };

    const url = overrideUrl ?? this.baseApiUrl;

    return this.httpClient.get<PagedList<TPage>>(url, options);
  }

  /**
   * Get a result
   * @param param The url
   * @returns The raw result
   */
    protected getRaw(param: string): Observable<any> {
      return this.httpClient.get( `${this.baseApiUrl}/${param}`, { responseType: 'text' });
    }

  /**
   * Get a single entity
   * @param id The entity Id
   * @returns The entity
   */
  protected get(id: any): Observable<T> {
    return this.httpClient.get<T>(this.idApiUrl(id));
  }

  /**
   * Create an entity
   * @param entity The entity to save
   * @returns
   */
  protected create<T>(entity: any, overrideUrl: string | undefined = undefined): Observable<T> {
    const url = overrideUrl ?? this.baseApiUrl;

    return this.httpClient.post<T>(url, entity);
  }

  /**
   * Update an entity
   * @param id The entity Id
   * @param entity The entity to save
   * @param additionalQueryParams Any additional query paramaters
   * @returns
   */
  protected update<T>(id: any, entity: T, additionalQueryParams: any | undefined = undefined): Observable<T> {
    let httpParams = new HttpParams();
    httpParams = BaseApiService.populateHttpParams(additionalQueryParams, httpParams);

    return this.httpClient.put<T>(`${this.baseApiUrl}/${id}`, entity, { params: httpParams });
  }

  /**
   * Delete an entity
   * @param id The entity Id
   * @returns
   */
  protected delete<T>(id: any, additionalQueryParams: any | undefined = undefined): Observable<T> {
    let httpParams = new HttpParams();
    httpParams = BaseApiService.populateHttpParams(additionalQueryParams, httpParams);

    return this.httpClient.delete<T>(`${this.baseApiUrl}/${id}`, { params: httpParams });
  }

  /**
   * Map the queryParams obj into httpParams
   * @param queryParams The query param object to map
   * @param httpParams The current Http Params
   * @returns An updated Http Params
   */
  protected static populateHttpParams(
    queryParams: any | undefined,
    httpParams: HttpParams
  ) {
    if (queryParams === undefined) {
      return httpParams;
    }

    if (queryParams !== undefined) {
      for (const key in queryParams) {
        if (queryParams[key] !== undefined) {
          httpParams = httpParams.append(key, queryParams[key]);
        }
      }
    }

    return httpParams;
  }
}
